"use client";

import { useState, Suspense, useMemo } from "react";
import Link from "next/link";
import Image from "next/image";
import { WiestLogo, WiestLogoWhite } from "../logos/wiest-logo";
import { WiestCamperLogo } from "../logos/wiest-camper-logo";
import AudiLogo from "../logos/audi-logo";
import { MENU_ITEMS, type MenuItemT } from "@/app/constants/menu-items";
import Container from "@/app/components/ui/container";
import { BookmarkIcon, BurgerMenuIcon } from "../icons";
import NavMenu from "./nav-menu";
import StaticNavLinks from "./static-nav-links";
import { NavButton } from "./nav-button";
import GlobalSearch from "./global-search";
import { usePublicPathname } from "@/app/hooks/use-public-pathname";
import { useBookmark } from "@/app/hooks/use-bookmark";
import { cn } from "@/lib/utils";
import { getBrandConfig, isBrandPath } from "@/app/config/brand-registry";

export type NavbarTheme = "auto" | "light" | "dark";

type NavbarServiceBrandLogoT = {
  id: string;
  name: string;
  logoUrl: string;
};

type NavbarProps = {
  theme?: NavbarTheme;
  menuItems?: MenuItemT[];
  defaultOpenSubmenuHref?: string;
  homeHref?: string;
  wiestLogoHref?: string;
  tenantDisplayName?: string;
  tenantLogo?: {
    src: string;
    alt: string;
    width: number;
    height: number;
  };
  serviceBrandLogos?: NavbarServiceBrandLogoT[];
};

export default function Navbar({
  theme = "auto",
  menuItems = MENU_ITEMS,
  defaultOpenSubmenuHref,
  homeHref = "/",
  wiestLogoHref,
  tenantDisplayName = "Wiest Group",
  tenantLogo,
  serviceBrandLogos = [],
}: NavbarProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pathname = usePublicPathname();
  const isGebrauchtwagenPage = pathname === "/gebrauchtwagen";
  const isWiestCamperPage = pathname.startsWith("/wiest-camper");
  const isGrosskundenPath = pathname.startsWith("/grosskunden");
  const isAudiPath = isBrandPath(pathname, "audi");
  const isDarkNavbar =
    theme === "dark" || (theme === "auto" && (isGrosskundenPath || isAudiPath));
  const { vehicleIds, contactPersonIds } = useBookmark();
  const logoHref = isAudiPath
    ? getBrandConfig("audi").routePrefix
    : tenantLogo
      ? homeHref
      : (wiestLogoHref ?? homeHref);

  // Calculate total bookmark count
  const bookmarkCount = useMemo(
    () => vehicleIds.length + contactPersonIds.length,
    [vehicleIds.length, contactPersonIds.length]
  );

  // Render navbar content
  const renderNavbarContent = () => (
    <div className="flex items-center justify-between py-3">
      {/* Logo Container */}
      <div className="flex items-center gap-3 md:gap-0">
        <Link
          href={logoHref}
          className="flex items-center"
          aria-label={`Zur Startseite von ${tenantDisplayName}`}
        >
          {isWiestCamperPage ? (
            <WiestCamperLogo className="w-24 md:w-auto" />
          ) : tenantLogo ? (
            <Image
              src={tenantLogo.src}
              alt={tenantLogo.alt}
              width={tenantLogo.width}
              height={tenantLogo.height}
              className="h-8 w-auto md:h-10"
              priority
            />
          ) : isDarkNavbar ? (
            <WiestLogoWhite className="w-26 md:w-32" />
          ) : (
            <WiestLogo className="w-26 md:w-32" />
          )}
        </Link>

        {isAudiPath && !isWiestCamperPage && (
          <Link
            href="https://www.audi-zentrum-darmstadt.audi/de.html"
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Audi"
            className="hidden items-center pl-3 lg:flex"
          >
            <AudiLogo
              fill={isDarkNavbar ? "white" : "black"}
              width={72}
              height={24}
            />
          </Link>
        )}

        {!isWiestCamperPage && serviceBrandLogos.length > 0 && (
          <div className="hidden items-center gap-6 pl-3 lg:flex">
            {serviceBrandLogos.map((brandLogo) => (
              <Image
                key={brandLogo.id}
                src={brandLogo.logoUrl}
                alt={brandLogo.name}
                width={110}
                height={40}
                className="h-9 w-auto object-contain"
              />
            ))}
          </div>
        )}

        {isGrosskundenPath && (
          <Image
            src="/grosskunden-logo.png"
            alt="Wiest Großkunden"
            width={240}
            height={60}
            className="hidden h-4 w-auto invert md:block"
            sizes="(min-width: 1024px) 340px, 280px"
            priority
          />
        )}
      </div>

      <div className="flex items-center">
        {!isAudiPath && (
          <>
            <GlobalSearch isDarkTheme={isDarkNavbar} />

            <NavButton
              variant="link"
              linkSize="lg"
              className={cn(
                "svg-crisp relative",
                isDarkNavbar && "text-current hover:bg-white/10"
              )}
              aria-label="Merkliste öffnen"
              asChild
            >
              <Link
                href="/bookmarks"
                prefetch={false}
                className="inline-flex h-full w-full items-center justify-center"
              >
                <BookmarkIcon />
                {bookmarkCount > 0 && (
                  <span
                    className={cn(
                      "bg-primary text-primary-foreground absolute top-0.5 right-1.5 flex h-4.5 min-w-4.5 items-center justify-center rounded-full px-1 text-[10px] font-semibold",
                      bookmarkCount > 9 && "px-1.5"
                    )}
                    aria-label={`${bookmarkCount} gespeicherte Bookmarks`}
                  >
                    {bookmarkCount > 99 ? "99+" : bookmarkCount}
                  </span>
                )}
              </Link>
            </NavButton>

            <NavButton
              variant="link"
              linkSize="lg"
              className={cn(
                "svg-crisp group relative z-50",
                isDarkNavbar && "text-current hover:bg-white/10"
              )}
              onClick={() => setIsMenuOpen(true)}
              aria-expanded={isMenuOpen}
              aria-controls="main-menu"
              aria-label="Hauptmenü öffnen"
            >
              <div className="pointer-events-none flex items-center gap-2 select-none">
                <BurgerMenuIcon className="size-6" />
                <span className="text-xs font-medium md:text-sm">Menu</span>
              </div>
            </NavButton>

            <Suspense fallback={null}>
              <NavMenu
                isOpen={isMenuOpen}
                onClose={() => setIsMenuOpen(false)}
                menuItems={menuItems}
                defaultOpenSubmenuHref={defaultOpenSubmenuHref}
                wiestLogoHref={wiestLogoHref ?? homeHref}
                tenantDisplayName={tenantDisplayName}
              />
            </Suspense>
          </>
        )}
      </div>
    </div>
  );

  return (
    <nav
      aria-label="Hauptnavigation"
      data-navbar-theme={isDarkNavbar ? "dark" : "light"}
      className={cn(
        isDarkNavbar && "bg-black text-white",
        "border-muted/20 border-b",
        isAudiPath && "border-b-0"
      )}
    >
      {/* SEO-freundliche statische Links (für Crawler sichtbar, für Nutzer über sr-only unsichtbar) */}
      <StaticNavLinks menuItems={menuItems} />

      <Container size={isGebrauchtwagenPage ? "huge" : "default"}>
        {renderNavbarContent()}
      </Container>
    </nav>
  );
}
